import { Injectable } from '@angular/core';
import * as yaml from 'js-yaml';
import { AppHttpService } from 'src/app/shared/services';
import { ReportService as RptService } from 'src/app/shared/services/report.service';
import { Project } from 'src/app/shared/models';
import { ParsedReportTemplate } from './models/mapped-report-config.model';

export interface ListMappedReportsParams {
  projectId: string;
  templateId: string;
  search?: string;
  from?: string;
  to?: string;
  start?: number;
  total?: number;
}

export interface ListMappedReportsResponse {
  data: any[];
  meta: { totalRecords: number };
}

export interface MappedReportInstanceDetail {
  dailyReportId: string;
  groupKey: string;
  formNumber: string;
  reportDate: string;
  projectNumber: string;
  site: { id: string; name: string; latitude: number; longitude: number } | null;
  html: string;
  geometries: GeoJSON.Feature[];
}

@Injectable({ providedIn: 'root' })
export class MappedReportService {
  constructor(private appHttp: AppHttpService, private rpt: RptService) {}

  async getMappedReportTemplates(projectId: string): Promise<ParsedReportTemplate[]> {
    const raw: any[] = await this.rpt.getReports(new Project({ id: projectId }), null, { include: ['project', 'organization'] });
    return (raw ?? []).reduce<ParsedReportTemplate[]>((acc, t) => {
      try {
        const parsed: any = yaml.load(t.template);
        if (parsed?.mapped_report?.enabled === true) {
          acc.push({ ...t, mapped_report: parsed.mapped_report });
        }
      } catch {
        // skip malformed YAML
      }
      return acc;
    }, []);
  }

  listMappedReports(params: ListMappedReportsParams): Promise<ListMappedReportsResponse> {
    // The mapped-report list is keyed by project in the path: /v1/mapped-report/project/:projectId.
    // Each row is a mapped-report instance = (daily report × bid-item-prefix group). The mapped-report
    // template uuid is REQUIRED via the reportId query param (it drives the API's collation config).
    // from/to/start/total filter the result set; search filters by form number.
    const qp: Record<string, any> = {
      reportId: params.templateId,
      start: params.start ?? 0,
      total: params.total ?? 50,
    };
    if (params.from)   qp.from   = params.from;
    if (params.to)     qp.to     = params.to;
    if (params.search) qp.search = params.search;

    return this.appHttp
      .getService(`/v1/mapped-report/project/${params.projectId}`, qp)
      .toPromise() as Promise<ListMappedReportsResponse>;
  }

  // Instance detail for the viewer: the rendered mapped-report HTML for this single
  // (daily report × bid-item-prefix group) plus its as-built GeoJSON features.
  // reportId is the mapped-report template uuid (drives the API collation).
  async getMappedReportInstanceDetail(
    dailyReportId: string,
    groupKey: string,
    reportId: string
  ): Promise<MappedReportInstanceDetail> {
    const resp: any = await this.appHttp
      .getService(`/v1/mapped-report/${dailyReportId}/${groupKey}`, { reportId })
      .toPromise();

    return {
      dailyReportId: resp?.daily_report_id ?? dailyReportId,
      groupKey: resp?.group_key ?? groupKey,
      formNumber: resp?.form_number ?? '',
      reportDate: resp?.report_date ?? '',
      projectNumber: resp?.project_number ?? '',
      site: resp?.site ?? null,
      html: resp?.html ?? '',
      geometries: Array.isArray(resp?.geometries) ? resp.geometries : [],
    };
  }

  buildDeepLinkUrl(projectId: string, dailyReportId: string, groupKey: string): string {
    return `/projects/${projectId}/forms/${dailyReportId}/${groupKey}`;
  }
}
